import { ErrorView } from "@/components/ui/layout/error/errorView"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/baseUi/tabs"
import { cn } from "@/modules/lib/utils"
import { word } from "@modules/core/utils/i18n"
import { MemberTabDayToDay, MemberTabDayToDaySkeleton } from "@modules/members/components/memberTabDayToDay"
import { MemberTabHistory, MemberTabHistoryLoading } from "@modules/members/components/memberTabHistory"
import { MemberTabProfile } from "@modules/members/components/memberTabProfile"
import { MemberStatus } from "@modules/members/gateway/memberDto"
import { useMember } from "@modules/members/hooks/useMember"
import { MemberTabOffersLayout } from "@modules/members/memberOffers/components/memberOffersLayout"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { ErrorCard } from "@modules/ui/components/error/errorCard"
import { HuAvatarCardSidebar } from "@modules/ui/components/huAvatarCardSidebar"
import { startTransition, Suspense } from "react"
import { ErrorBoundary } from "react-error-boundary"

type MemberDetailsInfoProps = {
  memberId: string
} & React.ComponentProps<"div">

export function MemberDetailsInfo({ memberId, className, ...props }: MemberDetailsInfoProps) {
  const { data: memberDetailed, error } = useMember(memberId)
  const { getCurrentStackState, setCurrentStackState } = useSidebar()
  const isArchivedMember = memberDetailed.status === MemberStatus.TERMINATED
  const activeIndex: string = getCurrentStackState("activeTabIndex") ?? (isArchivedMember ? "1" : "0")

  function selectTab(index: string) {
    startTransition(() => {
      setCurrentStackState("activeTabIndex", index)
    })
  }

  if (error) return <ErrorCard error={error} />

  return (
    <div className={cn(className)} {...props}>
      {/* HEADER */}

      <HuAvatarCardSidebar
        id={memberDetailed.userId}
        avatar={memberDetailed.publicProfile?.avatar ?? undefined}
        firstname={memberDetailed.firstname ?? ""}
        lastname={memberDetailed.lastname ?? ""}
        username={memberDetailed.username}
        prefixUsername={memberDetailed.username ? "@" : undefined}
        nationality={memberDetailed.publicProfile?.nationality ?? undefined}
        email={memberDetailed.email}
      />

      {/* NAVIGATION TABS */}
      <div className="mt-4 flex w-full">
        <Tabs defaultValue={activeIndex} className="flex w-full flex-col">
          <TabsList variant="default">
            {!isArchivedMember && (
              <TabsTrigger variant="default" onClick={() => selectTab("0")} value="0">
                {word("box.member.detailsInfo.dayToDay")}{" "}
              </TabsTrigger>
            )}
            <TabsTrigger variant="default" onClick={() => selectTab("1")} value="1">
              {word("box.member.detailsInfo.profile")}
            </TabsTrigger>
            <TabsTrigger variant="default" onClick={() => selectTab("2")} value="2">
              {isArchivedMember ? word("box.member.detailsInfo.offerHistory") : word("box.member.detailsInfo.offers")}
            </TabsTrigger>
            <TabsTrigger variant="default" onClick={() => selectTab("3")} value="3">
              {word("box.member.detailsInfo.history")}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="0" className="flex w-full">
            {memberDetailed ? <MemberTabDayToDay memberDetailed={memberDetailed} /> : <MemberTabDayToDaySkeleton />}
          </TabsContent>
          <TabsContent value="1" className="flex w-full">
            <MemberTabProfile memberDetailed={memberDetailed} />
          </TabsContent>
          <TabsContent value="2" className="flex w-full">
            <MemberTabOffersLayout memberDetailed={memberDetailed} />
          </TabsContent>
          <TabsContent value="3" className="flex w-full">
            <ErrorBoundary fallback={<ErrorView />}>
              <Suspense fallback={<MemberTabHistoryLoading />}>
                <MemberTabHistory memberId={memberDetailed.id} />
              </Suspense>
            </ErrorBoundary>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
